import React, { useState } from 'react';
import { RouteComponentProps, useHistory, useLocation, withRouter } from 'react-router-dom';
import {
  Button as ButtonComp,
  Flex,
  Box,
  Sidecar,
  IconButton,
  Icon,
  Text,
  IconName,
} from '@fullstory/components';
import css from './helpsidecar.module.scss';
import { useFetchSettings } from '@fullstory/redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { PlanStage } from '@fullstory/proto/fs/services/orgs';
import { useCSLink } from './useCSLink';
import { isFreePlan } from '@fullstory/util/plans';

const ensureTrailingSlash = (s: string) => (s.endsWith('/') ? s : `${s}/`);
const sidecarTrailingUrl: string = 'help';
const isHelpOpen = (s: string) =>
  s.endsWith(`/${sidecarTrailingUrl}`) || s.endsWith(`/${sidecarTrailingUrl}/`);

export const computeHelpSidecarPath = (pathname: string): string =>
  `${ensureTrailingSlash(pathname)}${sidecarTrailingUrl}`;

export const HelpSidecarNavigator = ({
  history,
  location,
}: Pick<RouteComponentProps, 'history' | 'location'>): void => {
  // prevent /help/help if user presses the help icon button multiple times.
  if (isHelpOpen(location.pathname)) return;
  history.replace({
    pathname: computeHelpSidecarPath(location.pathname),
    state: location.state,
    search: location.search,
  });
};

interface HelpSidecarButtonProps {
  iconButtonSize?: React.ComponentProps<typeof IconButton>['size'];
  showSeparator?: boolean;
}

export const HelpSidecarButton: React.FC<HelpSidecarButtonProps> = ({
  iconButtonSize = 'large',
  showSeparator = true,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const navigateToSidecarUrl = (): void => {
    HelpSidecarNavigator({ history, location });
  };

  return (
    <Flex data-component="HelpSidecarButton" testId="help-sidecar-button">
      <Box padding={[4, 0]} marginRight={8}>
        <IconButton
          title={intl.formatMessage({
            defaultMessage: 'Help',
            id: 'SENRqu',
          })}
          iconName="help-fill"
          onClick={navigateToSidecarUrl}
          size={iconButtonSize}
        />
      </Box>
      {showSeparator && <Box padding={[4, 0]} className={css.separator} />}
    </Flex>
  );
};

interface HelpSidecarLinkProps extends RouteComponentProps {
  text: string;
}

const HelpSidecarLink: React.FC<HelpSidecarLinkProps> = props => {
  const navigateToSidecarUrl = (): void => {
    HelpSidecarNavigator(props);
  };

  return (
    <ButtonComp data-component="HelpSidecarLink" onClick={navigateToSidecarUrl} variant="minimal">
      {props.text}
    </ButtonComp>
  );
};

const HelpSidecarLinkWithRouter = withRouter(HelpSidecarLink);

export { HelpSidecarLinkWithRouter as HelpSidecarLink };

interface HelpSidecarProps {
  closeFn: () => void;
  closeAfterFn?: () => void;
}

export const HelpSidecar: React.FC<HelpSidecarProps> = ({ closeFn, closeAfterFn }) => {
  const [visible, setVisible] = useState(true);
  const intl = useIntl();
  const settings = useFetchSettings();

  const closeFnWrapped = () => {
    closeFn();
    if (closeAfterFn) {
      closeAfterFn();
    }
  };

  const contactSupportLink = useCSLink();
  const showCustomerSuccessOfficeHours =
    settings?.isReady &&
    !isFreePlan(settings.data?.plan?.basePlan) &&
    settings.data?.plan?.planStage !== PlanStage.TRIAL_STAGE;

  return (
    <Sidecar
      data-component="HelpSidecar"
      open={visible}
      position="right"
      onClose={() => setVisible(false)}
      onClosed={closeFnWrapped}
    >
      <Box paddingTop={32} paddingBottom={32} paddingRight={16} paddingLeft={16}>
        <Flex column>
          <HelpSidecarHeaderText />
          <HelpSidecarReference
            caption={intl.formatMessage({
              defaultMessage: 'Find tutorials, articles, videos and more.',
              id: 'XLLRWW',
            })}
            iconName="life-preserver"
            link="https://help.fullstory.com/hc/en-us"
            text={intl.formatMessage({ defaultMessage: 'Knowledge Base', id: 'EbNaDn' })}
            bottomJoin
          />
          <HelpSidecarReference
            caption={intl.formatMessage({
              defaultMessage: 'Browse our library of on-demand trainings.',
              id: 'i25LSw',
            })}
            iconName="graduation-cap"
            link="https://help.fullstory.com/hc/en-us/articles/360032522334-Level-up-with-Fullstory"
            text={intl.formatMessage({
              defaultMessage: 'Level up with Fullstory training',
              id: '7/lZYM',
            })}
            bottomJoin
            topJoin
          />
          {showCustomerSuccessOfficeHours && (
            <HelpSidecarReference
              caption={intl.formatMessage({
                defaultMessage: 'New user training, office hours, live and on-demand webinars.',
                id: 'v02rA7',
              })}
              iconName="star-person"
              link="https://eventhub.goldcast.io/?eventHubId=8b839287-475e-4773-a016-ac43fd4e4672"
              text={intl.formatMessage({
                defaultMessage: 'Learn and engage with Fullstory experts',
                id: 'vLwWp5',
              })}
              topJoin
              bottomJoin
            />
          )}
          <HelpSidecarReference
            caption={intl.formatMessage({
              defaultMessage: 'Connect with other Fullstory users.',
              id: 'UGAWrS',
            })}
            iconName="people"
            link="https://help.fullstory.com/hc/en-us/community/topics"
            text={intl.formatMessage({
              defaultMessage: 'Ask the Community',
              id: 'BzB3mV',
            })}
            topJoin
            bottomJoin={!isFreePlan(settings.data?.plan?.basePlan)}
          />
          {!isFreePlan(settings.data?.plan?.basePlan) && (
            <HelpSidecarReference
              caption={intl.formatMessage({
                defaultMessage:
                  "Can't find what you're looking for? Our global support team is here to help.",
                id: 'dUXYfP',
              })}
              iconName="envelope"
              link={contactSupportLink}
              text={intl.formatMessage({
                defaultMessage: 'Email Customer Support',
                id: 'PymI05',
              })}
              topJoin
            />
          )}
        </Flex>
      </Box>
    </Sidecar>
  );
};

const HelpSidecarHeaderText: React.FC = () => (
  <Flex data-component="HelpSidecarHeaderText">
    <Box paddingBottom={24}>
      <Text appearance="heading3">
        <FormattedMessage defaultMessage="HELP" id="xwHzlY" />
      </Text>
    </Box>
  </Flex>
);

interface HelpSidecarReferenceProps {
  caption: string;
  iconName: IconName;
  link: string;
  text: string;
  topJoin?: boolean;
  bottomJoin?: boolean;
}

const HelpSidecarReference: React.FC<HelpSidecarReferenceProps> = ({
  caption,
  iconName,
  link,
  text,
  topJoin,
  bottomJoin,
}) => {
  const [showHoverArrow, setShowHoverArrow] = useState(false);

  const displayHoverArrow = () => {
    setShowHoverArrow(true);
  };
  const hideHoverArrow = () => {
    setShowHoverArrow(false);
  };

  const newTab = () => {
    window.open(link);
  };

  return (
    <Box
      data-component="HelpSidecarReference"
      width={1}
      padding={[24, 32, 24, 32]}
      className={
        topJoin && bottomJoin
          ? css.hscReferenceBoxTopBottom
          : topJoin
          ? css.hscReferenceBoxTop
          : bottomJoin
          ? css.hscReferenceBoxBottom
          : css.hscReferenceBox
      }
      onClick={newTab}
      onMouseEnter={displayHoverArrow}
      onMouseLeave={hideHoverArrow}
    >
      <Flex justifyContent="flex-start">
        <Box display="flex" alignItems="center">
          <Box marginRight={16}>
            <Icon name={iconName} size={20} color="default" />
          </Box>
          <Box display="flex" flexDirection="column" maxWidth={323} alignItems="flex-start">
            <Text appearance="bodyMediumBold">{text}</Text>
            <Text appearance="bodySmall" color="secondary">
              {caption}
            </Text>
          </Box>
        </Box>
        <Box style={{ marginLeft: 'auto' }}>
          {showHoverArrow ? (
            <Flex alignItems="flex-end">
              <Icon name="chevron-right" size={16} />
            </Flex>
          ) : null}
        </Box>
      </Flex>
    </Box>
  );
};
